import React, {useCallback, useState} from "react";
import {useOutletContext} from "react-router";
import {useMediaQuery} from "@mui/material";
import {Typography} from "@mui/material";
import Grid from "@mui/material/Grid";
import {ManagerPicker} from "../components/pickers/ManagerPicker";
import {RosterGrid} from "../components/grids/RosterGrid";
import {apiGet, getUserName, rosterGridTypes} from "../utils";

export const TradeContainer = () => {
    const {currentLeague} = useOutletContext();
    const [opponentName, setOpponentName] = useState(null);
    const [rosterRows, setRosterRows] = useState([]);
    const [rosterRowsAfter, setRosterRowsAfter] = useState([]);
    const [opponentRosterRows, setOpponentRosterRows] = useState([]);
    const [opponentRosterRowsAfter, setOpponentRosterRowsAfter] = useState([]);
    const [bankMeBefore, setBankMeBefore] = useState(0);
    const [bankMeAfter, setBankMeAfter] = useState(0);
    const [bankOppBefore, setBankOppBefore] = useState(0);
    const [bankOppAfter, setBankOppAfter] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingOpponent, setLoadingOpponent] = useState(true);

    const widthThreshold = opponentName ? 1510 : 300;
    const isMobile = useMediaQuery(`(max-width: ${widthThreshold}px)`);

    const fetchPayingContracts = useCallback(() => {
        setLoading(true);
        setRosterRows([]);
        setRosterRowsAfter([]);
        const pullProjectedData = currentLeague.season_phase === 'PREDRAFT';
        apiGet(`contract/owned/${getUserName()}${pullProjectedData ? '/projected' : ''}`, (ownedData) => {
            let total = ownedData.data.map(contract => contract.salary).reduce((a, b) => a + b);
            apiGet(`contract/penalty/${getUserName()}${pullProjectedData ? '/projected' : ''}`, (penaltyData) => {
                if (penaltyData.data) {
                    total += (penaltyData.data.map(contract => contract.penalty).reduce((a, b) => a + b, 0));
                    setBankMeBefore(total);
                    setBankMeAfter(total);
                }
                const rows = ownedData.data.concat(penaltyData.data);
                setRosterRows(rows);
                setRosterRowsAfter(rows);
                setLoading(false);
            });
        });
    }, [currentLeague]);

    const fetchOpponentPayingContracts = useCallback((managerName = null) => {
        setLoadingOpponent(true);
        fetchPayingContracts();
        setOpponentName(managerName);
        setOpponentRosterRows([]);
        setOpponentRosterRowsAfter([]);
        const pullProjectedData = currentLeague.season_phase === 'PREDRAFT';
        apiGet(`contract/owned/${managerName}${pullProjectedData ? '/projected' : ''}`, (ownedData) => {
            let total = ownedData.data.map(contract => contract.salary).reduce((a, b) => a + b);
            apiGet(`contract/penalty/${managerName}${pullProjectedData ? '/projected' : ''}`, (penaltyData) => {
                if (penaltyData.data) {
                    total += (penaltyData.data.map(contract => contract.penalty).reduce((a, b) => a + b, 0));
                    setBankOppBefore(total);
                    setBankOppAfter(total);
                }
                const rows = ownedData.data.concat(penaltyData.data);
                setOpponentRosterRows(rows);
                setOpponentRosterRowsAfter(rows);
                setLoadingOpponent(false);
            });
        });
    }, [fetchPayingContracts, currentLeague]);

    const handleTradeCheckboxClick = (row, checked) => {
        if (row.manager_name === getUserName() && checked) {
            addToOppAfter(row, true);
            removeFromMeAfter(row);
        } else if (row.manager_name === getUserName() && !checked) {
            removeFromOppAfter(row);
            addToMeAfter(row);
        } else if (row.manager_name === opponentName && checked) {
            addToMeAfter(row, true);
            removeFromOppAfter(row);
        } else if (row.manager_name === opponentName && !checked) {
            removeFromMeAfter(row);
            addToOppAfter(row);
        }
    };

    const addToMeAfter = (row, proposed) => {
        const newRow = JSON.parse(JSON.stringify(row));
        if (proposed) newRow.proposed = true;
        const newRosterRowsAfter = [...rosterRowsAfter, newRow];
        setRosterRowsAfter(newRosterRowsAfter);
        setBankMeAfter(bankMeAfter + (row.is_owned ? row.salary : row.penalty));
    };

    const removeFromMeAfter = (row) => {
        const newRosterRowsAfter = rosterRowsAfter.filter((r) => r.key !== row.key);
        setRosterRowsAfter(newRosterRowsAfter);
        setBankMeAfter(bankMeAfter - (row.is_owned ? row.salary : row.penalty));
    };

    const addToOppAfter = (row, proposed) => {
        const newRow = JSON.parse(JSON.stringify(row));
        if (proposed) newRow.proposed = true;
        const newOppRosterRowsAfter = [...opponentRosterRowsAfter, newRow];
        setOpponentRosterRowsAfter(newOppRosterRowsAfter);
        setBankOppAfter(bankOppAfter + (row.is_owned ? row.salary : row.penalty));
    };

    const removeFromOppAfter = (row) => {
        const newOppRosterRowsAfter = opponentRosterRowsAfter.filter((r) => r.key !== row.key);
        setOpponentRosterRowsAfter(newOppRosterRowsAfter);
        setBankOppAfter(bankOppAfter - (row.is_owned ? row.salary : row.penalty));
    };

    const justify = isMobile ? 'flex-start' : 'center';
    return (
        <div>
            <Grid container justifyContent={justify}>
                <table>
                    <tbody>
                    <tr>
                        <td/>
                        <td align="right">
                            <ManagerPicker fetchFunction={fetchOpponentPayingContracts} includeSelf={false}/>
                        </td>
                    </tr>
                    {opponentName &&
                        <tr>
                            <td><Typography variant="h6" style={{margin: 10}}>Before</Typography></td>
                            <td><Typography variant="h6" style={{margin: 10}}>After</Typography></td>
                        </tr>}
                    {opponentName &&
                        <tr>
                            <td style={{verticalAlign: 'top'}}>
                                <RosterGrid type={rosterGridTypes.TRADING} managerName={getUserName()}
                                            rosterOverride={rosterRows} spentOverride={bankMeBefore} loadingOverride={loading}
                                            onTradeCheckboxClick={handleTradeCheckboxClick}/>
                            </td>
                            <td style={{verticalAlign: 'top'}}>
                                <RosterGrid type={rosterGridTypes.STANDARD} managerName={getUserName()}
                                            rosterOverride={rosterRowsAfter} spentOverride={bankMeAfter} loadingOverride={loading}
                                            onTradeCheckboxClick={handleTradeCheckboxClick}/>
                            </td>
                        </tr>}
                    {opponentName &&
                        <tr>
                            <td><Typography variant="h6" style={{margin: 10}}>{opponentName} Before</Typography></td>
                            <td><Typography variant="h6" style={{margin: 10}}>{opponentName} After</Typography></td>
                        </tr>}
                    {opponentName &&
                        <tr>
                            <td style={{verticalAlign: 'top'}}>
                                <RosterGrid type={rosterGridTypes.TRADING} managerName={opponentName}
                                            rosterOverride={opponentRosterRows} spentOverride={bankOppBefore} loadingOverride={loadingOpponent}
                                            onTradeCheckboxClick={handleTradeCheckboxClick}/>
                            </td>
                            <td style={{verticalAlign: 'top'}}>
                                <RosterGrid type={rosterGridTypes.STANDARD} managerName={opponentName}
                                            rosterOverride={opponentRosterRowsAfter} spentOverride={bankOppAfter} loadingOverride={loadingOpponent}
                                            onTradeCheckboxClick={handleTradeCheckboxClick}/>
                            </td>
                        </tr>}
                    </tbody>
                </table>
            </Grid>
        </div>
    );
}